import { ref, computed, watch } from 'vue'
import { useApplicationsStore } from '@/stores/applications'
import { useReferrersStore } from '@/stores/referrers'

interface Referrer {
  id: number
  name: string
  reachability?: string
  is_active: boolean
  employers?: number[]
  default_referrer_for?: number[]
}

interface Application {
  referrer?: number | null
  open_role_details?: {
    employer?: number
    employer_details?: {
      name?: string
    }
  }
}

export function useReferrerManagement() {
  const applicationsStore = useApplicationsStore()
  const referrersStore = useReferrersStore()
  
  const selectedReferrerId = ref<string>('unassigned')
  const isUpdatingReferrer = ref(false)

  // Computed properties for referrers
  const companyReferrers = computed(() => {
    const referrers = referrersStore.referrers || []
    const application = applicationsStore.selectedApplication as Application
    const employerId = application?.open_role_details?.employer
    
    return referrers
      .filter((referrer: Referrer) => referrer.employers?.includes(employerId as number))
      .filter((referrer: Referrer) => referrer.is_active)
  })

  const defaultReferrer = computed(() => {
    const application = applicationsStore.selectedApplication as Application
    return companyReferrers.value.find((referrer: Referrer) => {
      if (!referrer.default_referrer_for) return false
      return referrer.default_referrer_for.includes(
        application?.open_role_details?.employer as number
      )
    })
  })

  const easyReferrers = computed(() => {
    return companyReferrers.value.filter((referrer: Referrer) => referrer.reachability === 'EASY')
  })

  const hardReferrers = computed(() => {
    return companyReferrers.value.filter((referrer: Referrer) => referrer.reachability === 'HARD')
  })

  const notSpecifiedReferrers = computed(() => {
    return companyReferrers.value.filter((referrer: Referrer) => !referrer.reachability)
  })

  // Initialize referrer ID from application data
  const initializeReferrerId = (application: Application | null) => {
    if (!application) {
      selectedReferrerId.value = 'unassigned'
      return
    }

    const referrerId = application.referrer
    if (referrerId === null || referrerId === undefined) {
      selectedReferrerId.value = 'unassigned'
    } else {
      selectedReferrerId.value = String(referrerId)
    }
  }

  // Update referrer
  const updateReferrer = async (applicationId: string, newReferrerId: string) => {
    const application = applicationsStore.selectedApplication as Application
    if (!application) return

    const initialReferrerId = application.referrer
    const initialReferrerIdStr =
      initialReferrerId === null || initialReferrerId === undefined
        ? 'unassigned'
        : String(initialReferrerId)

    if (newReferrerId === initialReferrerIdStr) return
    if (applicationsStore.isLoadingSelected) return

    let newReferrerIdForAPI: number | null
    if (newReferrerId === 'unassigned') {
      newReferrerIdForAPI = null
    } else {
      const parsed = parseInt(newReferrerId)
      newReferrerIdForAPI = isNaN(parsed) ? null : parsed
    }

    const currentReferrerId = application?.referrer
    if (newReferrerIdForAPI === currentReferrerId) return

    isUpdatingReferrer.value = true
    try {
      await applicationsStore.updateApplication(applicationId, {
        referrer: newReferrerIdForAPI as any
      })

      await applicationsStore.fetchApplicationById(applicationId)
      
      const updatedApplication = applicationsStore.selectedApplication as Application
      const actualReferrerId = updatedApplication?.referrer
      if (actualReferrerId === null || actualReferrerId === undefined) {
        selectedReferrerId.value = 'unassigned'
      } else {
        selectedReferrerId.value = String(actualReferrerId)
      }
    } catch (error) {
      console.error('Failed to update referrer:', error)
      selectedReferrerId.value = initialReferrerIdStr
    } finally {
      isUpdatingReferrer.value = false
    }
  }

  // Watch for referrer changes
  const setupReferrerWatcher = (applicationId: string | null) => {
    return watch(selectedReferrerId, async (newValStr, oldValStr) => {
      if (newValStr === oldValStr) return
      if (!applicationId) return
      
      await updateReferrer(applicationId, newValStr)
    })
  }

  return {
    selectedReferrerId,
    isUpdatingReferrer,
    companyReferrers,
    defaultReferrer,
    easyReferrers,
    hardReferrers,
    notSpecifiedReferrers,
    initializeReferrerId,
    updateReferrer,
    setupReferrerWatcher
  }
}